import type { CompProps, GenericFieldConfig } from '@neo/blocks/forms/types'
import { Label } from '@neo/primitives/label'
import { Textarea } from '@neo/primitives/textarea'
import { useFromNumbers } from 'v2/hooks/use-from-numbers'
import { ConvChannels } from 'v2/types/api/conversations'
import { AudioPrompt } from '../components/audio-prompt'
import { type EventCallback, EventCallbacks } from '../components/event-callbacks'
import { HoldMusicField, type HoldMusicValue } from '../components/hold-music-field'
import { NodevarList } from '../components/nodevar-list'
import { RecordingConfig } from '../components/recording-config'
import { promptData } from '../config/nodes.config'
import { v2validations } from '../config/validation.config'
import type { MultiPartyCallV2NodeData, SidebarComponentProps } from '../types'
import { GenericSidebarComponent } from './sidebar.base'

type FormValues = MultiPartyCallV2NodeData['config']

const mpcEventTypes = [
  { value: 'all', label: 'All Events' },
  { value: 'enter', label: 'Enter' },
  { value: 'exit', label: 'Exit' },
  { value: 'digits', label: 'Digits' },
  { value: 'record', label: 'Record' },
  { value: 'completed', label: 'Completed' },
  { value: 'ringing', label: 'Ringing' },
  { value: 'hangup', label: 'Hangup' },
]

const fields = (nodeVars: string[], fromNumbers: string[]): GenericFieldConfig<FormValues> => {
  const validations = v2validations({ nodeVars }).multi_party_call_v2.innerType()

  return [
    {
      name: 'name',
      label: 'Name',
      zodValidation: validations.shape.name,
      componentType: 'input',
      inputProps: {
        placeholder: 'Enter name',
      },
    },
    {
      name: 'from',
      label: 'From',
      zodValidation: validations.shape.from,
      componentType: 'input',
      inputProps: {
        placeholder: 'Enter caller ID',
        endIcon: (
          <NodevarList
            nodevars={[...fromNumbers, ...nodeVars]}
            className="pr-2"
            inputElementSelector="[name=from]"
          />
        ),
      },
    },
    {
      name: 'to',
      type: 'slot',
      element: ({ form }) => (
        <div className="col-span-2">
          <form.Field name="to">
            {(field) => (
              <div>
                <Label className="mb-1.5 block">To (one per line)</Label>
                <Textarea
                  value={(field.state.value as string[] | undefined)?.join('\n') ?? ''}
                  onChange={(e) => {
                    const lines = e.target.value.split('\n')
                    field.handleChange(lines)
                  }}
                  onBlur={(e) => {
                    const lines = e.target.value.split('\n').filter((line) => line.trim())
                    field.handleChange(lines)
                  }}
                  placeholder="Enter destination numbers (one per line)"
                  className="min-h-[80px]"
                />
              </div>
            )}
          </form.Field>
        </div>
      ),
    },
    {
      name: 'call_type',
      label: 'Call type',
      zodValidation: validations.shape.call_type,
      componentType: 'radioGroup',
      inputProps: {
        options: [
          { value: 'agent_dials', label: 'Agent dials' },
          { value: 'agent_added', label: 'Agent added' },
        ],
      },
    },
    {
      name: 'role',
      label: 'Role',
      zodValidation: validations.shape.role,
      componentType: 'select',
      inputProps: {
        options: [
          { value: 'agent', label: 'Agent' },
          { value: 'customer', label: 'Customer' },
          { value: 'supervisor', label: 'Supervisor' },
        ],
        placeholder: 'Select role',
      },
    },
    {
      name: 'transfer',
      label: 'Transfer type',
      zodValidation: validations.shape.transfer,
      componentType: 'radioGroup',
      inputProps: {
        options: [
          { value: 'warm', label: 'Warm transfer' },
          { value: 'cold', label: 'Cold transfer' },
        ],
      },
    },
    // Recording section
    {
      name: 'record',
      type: 'custom',
      component: ({ value, form }: CompProps<FormValues>) => (
        <RecordingConfig
          value={{
            record: (value as boolean) ?? false,
            record_mode: form.getFieldValue('record_mode') ?? 'always',
            record_condition: form.getFieldValue('record_condition') ?? '',
            recording_option: form.getFieldValue('recording_option') ?? 'all_together',
          }}
          onChange={(val) => {
            form.setFieldValue('record', val.record)
            form.setFieldValue('record_mode', val.record_mode)
            form.setFieldValue('record_condition', val.record_condition)
            if (val.recording_option) form.setFieldValue('recording_option', val.recording_option)
          }}
          nodeVars={nodeVars}
          showRecordingOptions
        />
      ),
      onChangeListenTo: ['record_mode', 'record_condition', 'recording_option'],
    },
    // Customer hold music
    {
      name: 'customer_hold_music',
      type: 'slot',
      element: ({ form }) => (
        <div className="col-span-2">
          <form.Field name="customer_hold_music">
            {(field) => (
              <HoldMusicField
                label="Customer hold music"
                value={field.state.value as HoldMusicValue}
                onChange={(val) => field.handleChange(val)}
                nodeVars={nodeVars}
                showNodeVars
              />
            )}
          </form.Field>
        </div>
      ),
    },
    // Agent hold music
    {
      name: 'agent_hold_music',
      type: 'slot',
      element: ({ form }) => (
        <div className="col-span-2">
          <form.Field name="agent_hold_music">
            {(field) => (
              <HoldMusicField
                label="Agent hold music"
                value={field.state.value as HoldMusicValue}
                onChange={(val) => field.handleChange(val)}
                nodeVars={nodeVars}
                showNodeVars
              />
            )}
          </form.Field>
        </div>
      ),
    },
    // Voicemail message
    {
      name: 'voicemail_message',
      type: 'slot',
      element: ({ form }) => (
        <div className="col-span-2">
          <form.Field name="voicemail_message">
            {(field) => (
              <AudioPrompt
                label="Voicemail drop message"
                value={field.state.value ?? promptData}
                arrayIndex={0}
                onChange={(data) => field.handleChange(data)}
                nodeVars={nodeVars}
              />
            )}
          </form.Field>
        </div>
      ),
    },
    // Extra headers
    {
      name: 'extra_headers',
      label: 'Custom SIP headers',
      componentType: 'textarea',
      inputProps: {
        placeholder: 'Enter custom SIP headers (comma-separated)',
        className: 'min-h-[60px] font-jetbrains text-xs-400',
      },
    },
    // Events section
    {
      name: 'events',
      type: 'slot',
      element: ({ form }) => (
        <div className="col-span-2">
          <form.Field name="events">
            {(field) => (
              <EventCallbacks
                events={(field.state.value as EventCallback[]) ?? []}
                onChange={(events) => field.handleChange(events)}
                eventTypes={mpcEventTypes}
                nodeVars={nodeVars}
                className="mt-4"
              />
            )}
          </form.Field>
        </div>
      ),
    },
  ]
}

export const MultiPartyCallV2SidebarComponent = (
  props: SidebarComponentProps<MultiPartyCallV2NodeData>,
) => {
  const { nodeVars } = props
  const { accountNumbers } = useFromNumbers(ConvChannels.CALL)
  const fromNumbers = accountNumbers?.map((number) => number.number) || []
  const formFields = fields(nodeVars, fromNumbers)

  return <GenericSidebarComponent fields={formFields} {...props} />
}
